
import { Component, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../../../shared/shared-imports';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiRoot, ApiEndPoints } from '../../../../../shared/constants/api-endpoints';
import { DocumentServiceType, FieldValue, ServiceName, SrActionType, SrIOName } from '../../../../../shared/constants/services.enum';
import { Utils } from '../../../../../Utils/utils';
import { GetCarnetNoRequest } from '../models/get-ata-carnet-request.model';
import { SharedService } from '../../../../../shared/services/shared.service';
import { ApiService } from '../../../../../core/services/api.service';
import { LookupKey } from '../../../../../shared/constants/lookup-keys';
import { ATARefundRequest } from '../../../../../shared/models/services-request/ata-refund.model';
import { RoutesPath } from '../../../../../shared/constants/routes-path';
import { SrStatus } from '../../../../../shared/constants/status.enum';
import { CarnetQueryRequest } from '../models/carnet-query.model';
import { SrSubmitSuccessModalComponent } from "../../../../../shared/components/sr-submit-success-modal/sr-submit-success-modal.component";
import { environment } from '../../../../../../environments/environment';
import { OptionFieldComponent } from '../../../../../shared/components/controls/option-field/option-field.component';
import { TextFieldComponent } from '../../../../../shared/components/controls/text-field/text-field.component';
import { CopyCarnetSrRequest } from '../models/copy-carnet-request.model';
import { SkeletonLoaderComponent } from "../../../../../shared/components/skeleton-loader/skeleton-loader.component";
import { FileComponent } from '../../../../../shared/components/controls/file/file.component';
import { FolderResponse, AllowedDocumentType } from '../../../../../shared/models/documents-response.model';
import { DigitalConsentModalComponent } from "../digital-consent-modal/digital-consent-modal.component";
import { customValidator, normalizeIban } from '../../../../../Utils/validator';
import { DcMethodValues } from '../../../../../shared/constants/dc-method.constants';
import { SecurityDepositRequest } from '../models/security-deposit-request.model';

@Component({
  selector: 'app-refund',
  imports: [ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS, SrSubmitSuccessModalComponent, SkeletonLoaderComponent, DigitalConsentModalComponent],
  templateUrl: './refund.component.html',
  styleUrl: './refund.component.scss'
})
export class RefundComponent implements OnDestroy {
  @ViewChildren('fileCmp') fileComponents!: QueryList<FileComponent>;
  @ViewChildren(TextFieldComponent) textFields!: QueryList<TextFieldComponent>;
  @ViewChildren(OptionFieldComponent) optionFields!: QueryList<OptionFieldComponent>;
  securityDepositRequest: SecurityDepositRequest = new SecurityDepositRequest();
  lookupKey = LookupKey;
  fieldValue = FieldValue;
  documentServiceType = DocumentServiceType;
  form: FormGroup;
  selected?: any = true;
  totalSteps: number = 4;
  step: number = 1;
  userLoginName: string = "";
  userCSN: any = "";
  srNumber: any = "";
  lang: any = "";

  loginUserDetail: any = "";
  showATAHolderType: boolean = false;
  srType: string = "";
  srSubType: string = "";
  loadingDoc: boolean = false;
  uploadedDocumentLabel: any = [];
  srInprgoressAwating: boolean = false;
  showNotes: boolean = false;
  // This method is used to initialize the class instance using fb, shared service, api, router, and route
  constructor(
    private fb: FormBuilder,
    private sharedService: SharedService,
    private api: ApiService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.lang = this.sharedService.getCurrentLang();
    this.loginUserDetail = this.sharedService.getUserProfile();
    this.userLoginName = this.sharedService.getUserProfile()['Login Name'];
    this.userCSN = this.sharedService.getCsnAccountId('csn');

    this.srType = ServiceName.ATA_CARNET;
    this.srSubType = ServiceName.REFUND_CARNET;

    this.form = this.fb.group({
      carnetNo: [null, [Validators.required]],
      accountName: ['', [Validators.required, Validators.maxLength(350), customValidator('safeText')]],
      bankName: [null, [Validators.required, Validators.maxLength(150)]],
      otherBankName: [null, [Validators.maxLength(150),customValidator('safeText')]],
      bankLocation: [null, [Validators.required, Validators.maxLength(100), customValidator('safeText')]],
      iban: ['', [Validators.required, Validators.maxLength(34), customValidator('iban')]],
      swift: ['', [Validators.required, Validators.maxLength(100), customValidator('safeText')]],
      comments: ['', [Validators.maxLength(500), customValidator('safeText')]],
      agree: [false, [Validators.requiredTrue]]
    });

    this.form.get('iban')?.valueChanges.subscribe((value: string | null) => {
      const normalizedValue = normalizeIban(value);
      if (value !== normalizedValue) {
        this.form.get('iban')?.setValue(normalizedValue, { emitEvent: false });
      }
    });

    // SR Number in the url
    const routeSrNumber = this.route.snapshot.params?.['sr_no'];
    const routeCsn = this.route.snapshot.params?.['csn'];
    if (routeCsn) {
      this.userCSN = routeCsn;
    }
    if (routeSrNumber) {
      this.srNumber = routeSrNumber;
    }
    // in case of draft
    if (sessionStorage.getItem("srNumber") && this.srNumber == "") {
      let storeSrNumber: any = sessionStorage.getItem("srNumber");
      this.srNumber = storeSrNumber ? storeSrNumber : "";
    }

    this.route.params.subscribe(params => {
      const srNo = params['sr_no'];
      const csn = params['csn'];
      if (csn) {
        this.userCSN = csn;
      }
      if (srNo && srNo !== this.srNumber) {
        this.srNumber = srNo;
        this.getSRDetail();
      }
    });

    if (this.srNumber) {
      this.getSRDetail();
    }

    if (!this.userCSN && !this.srNumber) {
      this.sharedService.toastMessage('INFO', 'NO_CSN_NUMBER', 'info');
      this.router.navigate([RoutesPath.DASHBOARD]);
      return;
    }

    if (!this.srNumber) {
      this.getMemberDetail();
      this.getUploadedDocumentsLabel();
      this.getATACarnetList();
      this.openFolder();
    }
  }

  // This method is used to get member detail
  getMemberDetail() {
    let requestPayload = {
      "body": {
        "ProcessName": "DC Get Member Details Web WF",
        "DC Channel": this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": DcMethodValues.ATA_CARNET_REFUND,
        "IOName": "DC Get Acc Details IO",
        "CSN": this.userCSN
      }
    }
    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.GET_MEMBER_DETAIL_BY_CSN, requestPayload)
      .subscribe({
        next: (response: any) => {
          if (response["Error Code"] == "" && response["Error Message"] == "") {
            const serviceRequest = response.SiebelMessage?.["DC Account Data Transfer"];
            this.form.patchValue({
              iban: normalizeIban(serviceRequest["DC Bank IBAN No"]),
              bankName: serviceRequest["DC Bank Name"],
              accountName: serviceRequest["DC Bank Account Holder"]
              //["DC Bank Account Number"]
            })
          }
        }
      })
  }

  carnetNumberList: any = [];
  loadingCarnetList: boolean = false;
  // This method is used to get ata carnet list
  getATACarnetList() {
    const request = new GetCarnetNoRequest();
    request.body.DCLoginName = this.userLoginName;
    request.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();
    request.body.DCIncomingHierarchy["ListOfDC Community Portal ATA SR IO"]["DC Community Portal ATA SR"].CSN = this.userCSN;
    request.body.DCIncomingHierarchy["ListOfDC Community Portal ATA SR IO"]["DC Community Portal ATA SR"]['SR Type'] = ServiceName.ATA_CARNET;
    request.body.DCIncomingHierarchy["ListOfDC Community Portal ATA SR IO"]["DC Community Portal ATA SR"]['Sub Type'] = ServiceName.REFUND_CARNET;

    this.api.post(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
      ApiEndPoints.GET_ATA_CARNET_DETAILS,
      request
    ).subscribe({
      next: (response: any) => {
        if ((response['Error Code'] == null || response['Error Code'] == "DC-VAL-ERR") && response["SiebelMessage"]?.["DC Community Portal ATA SR"]) {
          let result = Utils.checkIsArray(response["SiebelMessage"]["DC Community Portal ATA SR"]);

          const validCarnets = this.sharedService.getValidCarnetList(result);
          this.carnetNumberList = [...this.carnetNumberList, ...validCarnets];

          this.loadingCarnetList = false;
        } else {
          this.loadingCarnetList = false;
        }
      }, error: () => {
        this.loadingCarnetList = false;
      }
    })
  }

  getUploadedDocumentsLabel() {
    let request = {
      MemberNo: this.userCSN,
      ResponseDateFormat: "dd/MM/yyyy"
    }
    this.api.post(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.GET_UPLOAD_DOC,
      request
    ).subscribe({
      next: (response: any) => {
        if (response.return && response.return?.status == 'Success') {
          if (response.return?.docsList) {
            this.uploadedDocumentLabel = Utils.checkIsArray(response.return.docsList.doc);
          }
        }
      }
    })
  }

  // This method is used to start refund using carnet
  startRefund(carnet: any) {
    this.selected = carnet;
    this.form.get('carnetNo')?.setValue(this.selected.number);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  expiredCarnetMsg: boolean = false;
  technicalErrorMsg: boolean = false;
  isCarnetValidityNull: boolean = false;

  // This method is used to handle carnet change event using option
  onCarnetChange(option: any) {
    this.expiredCarnetMsg = false;
    this.errorMsg = "";
    this.technicalErrorMsg = false;
    this.copyCarnetErrorMsg = "";

    this.isCarnetValidityNull = false;
    if (!option?.selectedLov) {
      this.technicalErrorMsg = true;
      return;
    }
    // Need to check the carnet validity
    // const validityDateStr = option?.selectedLov['DC ATA Carnet Validity Dt'];
    // const today = new Date();
    // today.setHours(0, 0, 0, 0); // Normalize today's date

    // if (!validityDateStr) {
    //   this.isCarnetValidityNull = true;
    //   this.technicalErrorMsg = true;
    //   return;
    // }
    // const validityDate = new Date(validityDateStr);
    // validityDate.setHours(0, 0, 0, 0);
    // if (isNaN(validityDate.getTime())) {
    //   // Invalid date format
    //   this.expiredCarnetMsg = true;
    //   return;
    // }

    // if (validityDate < today) {
    //   // Date is before today → expired
    //   this.expiredCarnetMsg = true;
    //   return;
    // }

    let srNo = option?.selectedLov["SR Number"];
    if (this.srNumber && srNo) {
      this.loadingSrDetail = true;
      this.copyCarnetNoDataToSR();
    } else {
      if (srNo) {
        let formData = this.form.getRawValue();
        this.loadingSrDetail = true;
        this.submitSRData(formData, SrActionType.DRAFT, true);
      }
    }
  }
  copyCarnetErrorMsg: string = '';
  securityAmount: any = 0;
  // This method is used to copy carnet number data to sr
  copyCarnetNoDataToSR() {
    let selectedCarNetSrNo: any = this.carnetNumberList?.find((c: any) => c.Value === this.form.get("carnetNo")?.value)?.["SR Number"];

    if (!selectedCarNetSrNo) {
      return;
    }
    this.securityAmount = 0;
    const copyCarnetReq = new CopyCarnetSrRequest();
    copyCarnetReq.body.DCLoginName = this.userLoginName;
    copyCarnetReq.body.DCToSRNumber = this.srNumber;
    copyCarnetReq.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();
    let dcPortal = copyCarnetReq.body.DCIncomingHierarchy["ListOfDC Community Portal SR Upsert IO"]["DC Community Portal SR"];
    dcPortal["SR Number"] = selectedCarNetSrNo;
    dcPortal.CSN = this.userCSN;
    dcPortal['SR Type'] = this.srType;
    dcPortal['Sub Type'] = this.srSubType;

    this.api.post(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
      ApiEndPoints.ATA_MANAGE_SERVICE_REQUEST,
      copyCarnetReq
    ).subscribe({
      next: (response: any) => {
        if ((response['Error Code'] == null || response['Error Code'] == "DC-VAL-ERR") && response["SiebelMessage"]?.["DC Community Portal SR"] && response["Error Message"] == null || response["Error Message"] == "") {
          this.step = 2;
          this.openFolder();
          this.getSRDetail();
        } else {
           this.form.get("carnetNo")?.setValue(null);
          this.loadingSrDetail = false;
          this.copyCarnetErrorMsg = response['Error Message'];
        }
      }
    })
  }

  allFields: any = [];
  // This method is used to move to the next step
  nextStep(): void {
    // if (this.isCarnetValidityNull || this.expiredCarnetMsg) {
    //   return;
    // }
    const stepFieldsMap: { [key: number]: string[] } = {
      1: ['carnetNo'],
      2: [
        'accountName',
        'bankName',
        'otherBankName',
        'bankLocation',
        'iban',
        'swift',
        'comments', 'agree'
      ]
    };

    if (this.textFields && this.optionFields) {
      this.allFields = [...this.textFields.toArray(), ...this.optionFields.toArray()];
    }

    if (this.step > 1) {
      this.isFromSumit = true;
    }

    if (!environment.ignoreValidators) {
      // Validate all steps up to the current step
      for (let i = 1; i <= this.step; i++) {
        const fieldsToCheck = stepFieldsMap[i] || [];
        if (Utils.handleInvalidStep(this.form, this.allFields, fieldsToCheck)) {
          this.form.markAllAsTouched();
          return;
        }
      }
    }

    if (this.step >= 2) {
      if (this.form.invalid) {
        const invalidControls = Utils.getInvalidControls(this.form);
        console.warn('Invalid controls:', invalidControls);
        this.form.markAllAsTouched();
        Utils.focusFirstInvalidControl(this.form, this.allFields);
        return;
      }
      if (this.form.invalid || this.checkRequiredDocuments()) {
        this.sharedService.toastMessage('ERROR', "FILL_REQUIRED_FIELDS_DOCUMENTS", 'error', false);
        return;
      }
      this.openTermsModal();
    }

    if (this.step < 3) {
      this.step++;
    }
  }


  //get folder id and attachments
  srFolder!: FolderResponse;
  mainFromSectionDocs: AllowedDocumentType[] = [];
  openFolder() {
    this.loadingDoc = true;
    const isLabels = !this.srNumber;

    this.sharedService.getDocuments(this.srNumber, DocumentServiceType.ATA_REFUND, isLabels)
      .subscribe({
        next: (response: any) => {
          if (response.success == true) {
            this.loadingDoc = false;
            this.srFolder = response.data ? response.data : response;
            // Map for quick lookup
            const uploadedMap = new Map(
              (this.uploadedDocumentLabel || []).map((u: any) => [u.docLabel?.trim(), u.creationDate])
            );

            const today = new Date();
            this.srFolder.allowedDocumentTypes = this.srFolder.allowedDocumentTypes?.map((doc: any) => {
              const label = doc.nameEn?.trim() || "";
              const uploadedDateStr: any = uploadedMap.get(label);

              if (uploadedDateStr) {
                // If uploaded, determine mandatory based on type
                if (label === 'Trade_License_Copy') {
                  // Split into parts
                  const [day, month, year] = uploadedDateStr.split('/').map(Number);
                  const parsedDate = new Date(year, month - 1, day);

                  // const parsedDate = new Date(uploadedDateStr);
                  const diffInDays = (today.getTime() - parsedDate.getTime()) / (1000 * 60 * 60 * 24);
                  // Within 1 year = not mandatory
                  doc.mandatory = diffInDays <= 365 ? false : true;

                } else {
                  doc.mandatory = false;
                }
              }
              return doc;
            });
            this.mainFromSectionDocs = this.srFolder.allowedDocumentTypes?.filter((d: any) => !d.forUISection);

          }
        }
      });
  }

  // This method is used to check required documents
  checkRequiredDocuments(): boolean {
    if (!this.mainFromSectionDocs || this.mainFromSectionDocs.length === 0) {
      return false;
    }
    const fileCmps = this.fileComponents && typeof this.fileComponents.toArray === 'function'
      ? this.fileComponents.toArray()
      : [];

    // ViewChildren not ready or not fully rendered yet – defer document checks
    if (fileCmps.length < this.mainFromSectionDocs.length) {
      return false;
    }

    let hasMissing = false;
    for (let index = 0; index < this.mainFromSectionDocs.length; index++) {
      const doc = this.mainFromSectionDocs[index];
      const cmp = fileCmps[index];
      if (!cmp) continue;

      if (typeof cmp.checkMissing === 'function' && cmp.checkMissing((doc)?.mandatory)) {
        hasMissing = true;
      }
    }
    return hasMissing;
  }


  isFromSumit: boolean = false;
  formSubmitLoader: boolean = false;
  isSaved: boolean = false;
  successModal: boolean = false;
  showActions: boolean = true;
  loadingSrDetail: boolean = false;

  // This method is used to get sr detail
  private getSRDetail() {
    this.loadingSrDetail = true;
    const queryReq = new CarnetQueryRequest();

    queryReq.body.DCLoginName = this.userLoginName || "PRAKASHPOOJARY";
    queryReq.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();
    let portalSr = queryReq.body.DCIncomingHierarchy["ListOfDC Community Portal SR IO"]["DC Community Portal SR"];
    portalSr["SR Number"] = this.srNumber;
    portalSr.CSN = this.userCSN || "";
    portalSr['SR Type'] = this.srType;
    portalSr['Sub Type'] = this.srSubType;

    this.api.post(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
      ApiEndPoints.ATA_QUERY_SERVICEREQUEST,
      queryReq
    ).subscribe({
      // This method is used to handle the next callback using response
      next: (response: any) => {
        this.loadingSrDetail = false;
        if ((response['Error Code'] == null || response['Error Code'] == "DC-VAL-ERR") && response?.SiebelMessage
          && response?.SiebelMessage["DC Community Portal SR"] && (response["Error Message"] == null || response["Error Message"] == "")) {

          let srData = response?.SiebelMessage["DC Community Portal SR"];
          this.userCSN = srData?.["DC Member Num"] || this.userCSN;
          this.securityAmount = srData?.['DC ATA Par Sec Dep Amt'];
          
          this.getUploadedDocumentsLabel();
          if (!this.carnetNumberList?.length && this.userCSN) {
            this.getATACarnetList();
          }
          let fields = {
            carnetNo: srData['DC Carnet Number'],
            accountName: srData?.["DC Account Holder"] ?? null,
            bankName: srData?.["DC Holder Bank Name"] ?? null,
            bankLocation: srData?.["DC Bank Loc"] ?? null,
            iban: normalizeIban(srData?.["DC IBAN No"]),
            swift: srData?.["DC Swift Code"] ?? null,
            comments: srData?.["DC ATA Reason"] ?? null,
            agree: true
          }
          this.step = 2;
          this.form.patchValue(fields);

          if (this.sharedService.enableNotesOption(srData["Status"])) {
            this.srInprgoressAwating = true;
            this.showNotes = true;
          }

          if (srData["Status"] != SrStatus.Draft && srData["Status"] != SrStatus.AwaitingClarification && srData["Status"] != SrStatus.Open) {
            this.showActions = false;
            this.step = 3;
            this.form.disable(); // disables all fields in the form
          }
          this.openFolder();
        } else {
          this.sharedService.toastMessage('ERROR', '', 'error', response["Error Message"]);
        }
      },
      // This method is used to handle the error callback
      error: () => {
        this.loadingSrDetail = false
      }
    })
  }

  // This method is used to save and close
  saveAndClose(): void {
    const invalidControls = Utils.getInvalidControls(this.form);
    console.warn('Invalid controls:', invalidControls);
    const formFieldData = this.form.getRawValue();
    this.submitSRData(formFieldData, SrActionType.SAVE);
  }
  errorMsg: string = "";
  // This method is used to submit sr data using form data, action type, and is carnet no changed
  private submitSRData(formData: any, actionType: string, isCarnetNoChanged: boolean = false) {
    this.errorMsg = "";
    this.technicalErrorMsg = false;
    let isActionSubmit = actionType == SrActionType.SUBMIT;
    let isActionSave = actionType == SrActionType.SAVE;
    let isActionDraft = actionType == SrActionType.DRAFT;
    
    if (Utils.hasMaxLengthError(this.form)) {
      this.form.markAllAsTouched();
      return;
    }
    if (isActionSubmit && (this.form.invalid || this.checkRequiredDocuments() || this.loadingDoc)) {
      this.sharedService.toastMessage('ERROR', "FILL_REQUIRED_FIELDS_DOCUMENTS", 'error', false);
      return;
    }

    let bankName = "";
    if (formData?.bankName === FieldValue.OTHER_BANK || formData?.bankName === FieldValue.OTHER_BANK_AR) {
      bankName = formData.otherBankName;
    } else {
      bankName = formData.bankName;
    }
    const refundReq = new ATARefundRequest();
    refundReq.body.DCLoginName = this.userLoginName;
    refundReq.body.DCMethod = isActionSubmit ? "SUBMIT" : "SAVEASDRAFT";
    refundReq.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();
    let portalSr = refundReq.body.DCIncomingHierarchy["ListOfDC Community Portal SR Upsert IO"]["DC Community Portal SR"]
    portalSr["SR Number"] = this.srNumber ?? "";
    portalSr["CSN"] = this.userCSN;
    portalSr['SR Type'] = this.srType;
    portalSr['Sub Type'] = this.srSubType;
    portalSr["DC Payment Method"] = this.lang == 'ar' ? FieldValue.PAYMENT_METHOD_ONLINE_AR : FieldValue.PAYMENT_METHOD_ONLINE_EN;
    portalSr['DC Account Holder'] = formData.accountName ?? "";
    portalSr['DC Holder Bank Name'] = bankName ?? "";
    portalSr['DC IBAN No'] = normalizeIban(formData.iban);
    portalSr['DC Channel'] = this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN;

    if (isCarnetNoChanged == false) {
      portalSr['DC Carnet Number'] = formData.carnetNo ?? "";
    }
    portalSr["DC Bank Loc"] = formData.bankLocation ?? "";
    portalSr["DC Swift Code"] = formData.swift ?? "";
    portalSr["DC ATA Reason"] = formData.comments ?? "";
    portalSr["DC ATA Consent"] = "N";
    portalSr["DC ATA Pledged Letter Consent"] = "N";
    portalSr["DC Bank Detail Consent"] = "Y";

    if (isActionSubmit && this.expiredCarnetMsg) {
      return;
    }
    this.formSubmitLoader = true;
    this.technicalErrorMsg = false;
    this.showActions = false;
    this.api.post(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
      ApiEndPoints.ATA_MANAGE_SERVICE_REQUEST,
      refundReq
    ).subscribe({
      // This method is used to handle the next callback using response
      next: (response: any) => {
        if ((response['Error Code'] == null || response['Error Code'] == "DC-VAL-ERR") && response["DCSRNumber"] && response["Error Message"] == null || response["Error Message"] == "") {
          this.isSaved = true;
          this.showActions = true;
          if (isActionSave || isActionDraft) {
            if (isActionDraft && this.srNumber == "") {
              this.srNumber = response["DCSRNumber"];
              sessionStorage.setItem("srNumber", response["DCSRNumber"]);
              if (isCarnetNoChanged) {
                this.copyCarnetNoDataToSR();
              }
            } else {
              this.srNumber = response["DCSRNumber"];
              if (isActionSave) {
                this.successModal = true;
              }
            }
          } else {
            this.srNumber = response["DCSRNumber"];
            //set sr detail for success page to get ioname
            const { service, serviceAr } = Utils.getServiceLabels(this.srSubType, this.lang);
            let srDetailObj = {
              serviceSubTypeEn: service,
              serviceSubTypeAr: serviceAr,
              mainService: this.srType,
              service: this.srSubType,
              IOName: SrIOName.ISSUE_NEW_ATA_CARNET,
              srNumber: this.srNumber,
              totalSteps: this.totalSteps,
              step: this.step
            }
            this.sharedService.setSrResponse(srDetailObj);

            this.sharedService.toastMessage('SUCCESS', 'REQUEST_SUBMITTED', 'success');
            sessionStorage.removeItem("srNumber");
            this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.SUCCESS_PAGE + "/" + this.srNumber}`]);
          }
          this.formSubmitLoader = false;
        } else {
          this.showActions = true;
          this.formSubmitLoader = false;
          if (response['Error Code'] == 'DC-VAL-ERR' && response["Error Message"] != null) {
            this.errorMsg = response['Error Message'];
          } else {
            this.technicalErrorMsg = true;
          }
        }
      },
      // This method is used to handle the error callback using e
      error: (e) => {
        this.showActions = true;
        this.formSubmitLoader = false;
        this.technicalErrorMsg = true;
      }
    })
  }
  // This method is used to calculate form progress
  get formProgress() {
    if (this.form.disabled) {
      return 100;
    }
    if (!this.form.invalid) {
      if (!this.checkRequiredDocuments()) {
        return 100;
      }
    }
    return Utils.calculateFormProgress(this.step, this.totalSteps)
  }
  // This method is used to close success modal
  closeSuccessModal() {
    this.successModal = false;
    sessionStorage.removeItem("srNumber");
    this.router.navigate([RoutesPath.DASHBOARD]);
  }

  // This method is used to open terms condition 
  isTermsCondition: boolean = false
  openTermsCondition() {
    this.isTermsCondition = true;
    const formData = this.form.getRawValue();
    this.submitSRData(formData, SrActionType.DRAFT);

    let url = `${RoutesPath.SERVICE}/${RoutesPath.ATA.TERMS}`
    this.router.navigate([url]);
  }
  // This method is used to handle bank change event using event
  onBankChange(event: any) {
    if (event?.selectedLov?.Value === FieldValue.OTHER_BANK || event?.selectedLov?.Value === FieldValue.OTHER_BANK_AR) {
      this.form.get('otherBankName')?.setValidators([Validators.required, Validators.maxLength(150), customValidator('safeText')]);
    }
  }

  termsModal = false;
  consentModalData: any;
  // This method is used to open terms modal
  openTermsModal() {
    this.consentModalData = {
      SRSubType: this.srSubType,
      companyName: this.loginUserDetail['Name English'] ? this.loginUserDetail['Name English'] :`${this.loginUserDetail['First Name']} ${this.loginUserDetail['Last Name']}`,
      language: this.lang,
      reasonOrPurpose: this.form.getRawValue().comments ? this.form.getRawValue().comments : '',
      srNumber: this.form.value.srNumber ? this.form.value.srNumber : ''
    }
    this.termsModal = true;
  }

  // This method is used to submit pledge using value
  submitPledge(value: any) {
    this.termsModal = false;
    if (value == true) {
      const formData = this.form.getRawValue();
      this.submitSRData(formData, SrActionType.SUBMIT);
    }
  }

  // This method is used to handle the on destroy lifecycle hook
  ngOnDestroy(): void {
    if (!this.isTermsCondition) {
      sessionStorage.removeItem("srNumber");
    }
  }
}
